import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/api/client";
import TopBar from "@/components/TopBar";
import NotificationBanner from "@/components/NotificationBanner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const isFullIsoDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(new Date(s));

export default function AttendanceForm() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialDate = params.get("date") || today();
  // `date` is the COMMITTED value used for backend fetches. `dateInput` is
  // the live input value while the user is typing. Committing only on Enter
  // or blur (item 7 bug fix) — prevents mid-typing partial dates like an
  // intermediate 07 → 06 keystroke from firing a fetch that returns the
  // wrong (and often already-approved) row.
  const [date, setDate] = useState(initialDate);
  const [dateInput, setDateInput] = useState(initialDate);
  const commitDate = () => {
    if (isFullIsoDate(dateInput) && dateInput !== date) setDate(dateInput);
  };
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [brk, setBrk] = useState(60);
  const [remarks, setRemarks] = useState("");
  const [carNumber, setCarNumber] = useState("");
  const [km, setKm] = useState("");
  const [standby, setStandby] = useState(false);
  const [existing, setExisting] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    setDateInput(date);
    api.get(`/attendance/me`).then((r) => {
      const found = r.data.find((a) => a.work_date === date);
      if (found) {
        setStart(found.start_time || "");
        setEnd(found.end_time || "");
        setBrk(found.break_minutes != null && found.break_minutes !== 0 ? found.break_minutes : 60);
        setRemarks(found.remarks || "");
        setCarNumber(found.car_number || "");
        setKm(found.kilometers != null ? String(found.kilometers) : "");
        setStandby(!!(found.remarks && found.remarks.includes("待機")));
        setExisting({ status: found.status, review_note: found.review_note });
      } else {
        setStart(""); setEnd(""); setBrk(60); setRemarks(""); setCarNumber(""); setKm(""); setStandby(false);
        setExisting(null);
      }
    }).catch(() => {});
  }, [date]);

  const computed = (() => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm) - (Number(brk) || 0);
    if (mins < 0) mins += 24 * 60;
    return Math.round((mins / 60) * 100) / 100;
  })();

  const submit = async (asSubmit) => {
    setErr(""); setMsg("");
    // Item 3: On SUBMIT (not draft), Time-In, Time-Out and Break are mandatory.
    if (asSubmit) {
      const missing = [];
      if (!start) missing.push(t("driver.startTime"));
      if (!end) missing.push(t("driver.endTime"));
      if (brk === "" || brk === null || brk === undefined) missing.push(t("driver.breakMinutes"));
      if (missing.length) {
        setErr(t("common.requiredFields", { fields: missing.join(", ") }));
        return;
      }
    }
    if (asSubmit && standby && !remarks.trim()) {
      setErr(t("driver.rulesStandbyRequired"));
      return;
    }
    try {
      await api.post("/attendance", {
        work_date: date,
        start_time: start || null,
        end_time: end || null,
        break_minutes: Number(brk) || 0,
        remarks: remarks || null,
        car_number: carNumber || null,
        kilometers: km === "" ? null : Number(km),
        submit: asSubmit,
      });
      setMsg(asSubmit ? (existing?.status === "returned" ? t("driver.resubmitted") : t("driver.submitted")) : t("driver.savedDraft"));
      const r = await api.get("/attendance/me");
      const found = r.data.find((a) => a.work_date === date);
      if (found) setExisting({ status: found.status, review_note: found.review_note });
      // Auto-fade the toast after 3.5 s so it doesn't linger.
      setTimeout(() => setMsg(""), 3500);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const isReturned = existing?.status === "returned";
  const isApproved = existing?.status === "approved";
  const submitLabel = isReturned ? t("driver.editAndResubmit") : t("driver.submitAttendance");
  const warnHours = computed > 8;
  const warnBreak = brk != null && Number(brk) < 60;

  return (
    <div className="shell">
      <TopBar />
      <div className="container" style={{ maxWidth: 640 }}>
        <button className="btn btn-outline" onClick={() => nav(-1)} style={{ marginBottom: 12 }}>← {t("common.back")}</button>
        <h1 className="page-title">{t("driver.clockIn")} / {t("driver.clockOut")}</h1>

        <NotificationBanner testIdPrefix="att-notif" style={{ marginBottom: 16 }} />

        {/* Rules banner */}
        <div className="rules-banner" data-testid="rules-banner" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <Info size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{t("driver.rulesTitle")}</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55 }}>
                <li>{t("driver.rule1")}</li>
                <li>{t("driver.rule2")}</li>
                <li>{t("driver.rule3")}</li>
              </ul>
            </div>
          </div>
        </div>

        {existing && (
          <div data-testid="att-status-banner" className={`banner banner-${existing.status}`} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700 }}>{t("common.status")}:</span>
              <span className={`tag tag-${existing.status}`}>{t(`common.${existing.status}`)}</span>
              {isReturned && <span style={{ fontSize: 13 }}>{t("driver.returnedHint")}</span>}
              {isApproved && <span style={{ fontSize: 13 }}>{t("driver.approvedHint")}</span>}
            </div>
            {existing.review_note && (
              <div style={{ marginTop: 8, fontSize: 13, color: "#334155", background: "#fff", padding: 8, borderRadius: 6 }}>
                <strong>{t("common.note")}:</strong> {existing.review_note}
              </div>
            )}
          </div>
        )}

        <div className="card"><div className="card-body">
          <div className="field">
            <label className="label">{t("common.date")}</label>
            <input
              data-testid="att-date" type="date" className="input"
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
              onBlur={commitDate}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitDate(); e.currentTarget.blur(); } }}
              disabled={isApproved}
            />
          </div>
          <div className="grid grid-2">
            <div className="field">
              <label className="label">{t("driver.startTime")} <span style={{ color: "#DC2626" }}>*</span></label>
              <input data-testid="att-start" type="time" className="input" value={start} onChange={(e) => setStart(e.target.value)} disabled={isApproved} />
            </div>
            <div className="field">
              <label className="label">{t("driver.endTime")} <span style={{ color: "#DC2626" }}>*</span></label>
              <input data-testid="att-end" type="time" className="input" value={end} onChange={(e) => setEnd(e.target.value)} disabled={isApproved} />
            </div>
          </div>
          <div className="grid grid-2">
            <div className="field">
              <label className="label">{t("driver.breakMinutes")} <span style={{ color: "#DC2626" }}>*</span></label>
              <input data-testid="att-break" type="number" min="0" className="input" value={brk} onChange={(e) => setBrk(e.target.value)} disabled={isApproved} />
              {warnBreak && <div style={{ color: "#F59E0B", fontSize: 12, marginTop: 4 }}><AlertTriangle size={12} /> {t("driver.warnBreak")}</div>}
            </div>
            <div className="field">
              <label className="label">{t("driver.workingHours")}</label>
              <div className="input" style={{ background: warnHours ? "#FEF3C7" : "#F1F5F9" }} data-testid="att-hours">{computed} h {warnHours && <span style={{ color: "#F59E0B", fontSize: 12 }}><AlertTriangle size={12} /> {t("driver.warnHours")}</span>}</div>
            </div>
          </div>
          <div className="grid grid-2">
            <div className="field">
              <label className="label">{t("driver.carNumber")}</label>
              <input data-testid="att-car" className="input" value={carNumber} onChange={(e) => setCarNumber(e.target.value)} placeholder="例: 品川 500 あ 1234" disabled={isApproved} />
            </div>
            <div className="field">
              <label className="label">{t("driver.kilometers")}</label>
              <input data-testid="att-km" type="number" min="0" step="0.1" className="input" value={km} onChange={(e) => setKm(e.target.value)} disabled={isApproved} />
            </div>
          </div>
          <div className="field">
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" data-testid="att-standby" checked={standby} onChange={(e) => { setStandby(e.target.checked); if (e.target.checked && !remarks.includes("待機")) setRemarks((r) => (r ? r + " 待機" : "待機")); }} disabled={isApproved} />
              <span>{t("driver.standbyLabel")}</span>
            </label>
          </div>
          <div className="field">
            <label className="label">{t("common.remarks")} {standby && <span style={{ color: "#DC2626" }}>*</span>}</label>
            <textarea data-testid="att-remarks" className="textarea" value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={isApproved} placeholder={standby ? t("driver.rulesStandbyRequired") : ""} />
          </div>
          {err && <div style={{ color: "#DC2626", marginBottom: 10 }} data-testid="att-err">{err}</div>}
          {msg && (
            <div data-testid="att-msg" style={{
              background: "#DCFCE7", border: "1px solid #16A34A", color: "#166534",
              padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontWeight: 600,
            }}>
              ✅ {msg}
            </div>
          )}
          {!isApproved && (
            <div className="row-actions">
              <button data-testid="att-save-draft" className="btn btn-outline" onClick={() => submit(false)}>{t("driver.saveDraft")}</button>
              <button data-testid="att-submit" className={isReturned ? "btn btn-warning" : "btn btn-primary"} onClick={() => submit(true)}>{submitLabel}</button>
            </div>
          )}
        </div></div>
      </div>
    </div>
  );
}
